import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { api, MARKET_META } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import {
    SEO_MARKETS_BY_KEY,
    buildCommonNumberCopy,
    commonNumberPath,
    resultTodayPath,
    previousResultPath,
} from "../lib/seoMarkets";

const SITE_URL = "https://shillongmorningteerresulttoday.com";

const todayIST = () =>
    new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
    });

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const GameCommonNumber = ({ marketKey }) => {
    const meta = SEO_MARKETS_BY_KEY[marketKey];
    const cardMeta = MARKET_META[marketKey];
    const copy = useMemo(() => buildCommonNumberCopy(meta), [meta]);
    const slug = meta.urlPrefix;
    const path = commonNumberPath(slug);

    useSeo({
        title: copy.title,
        description: copy.description,
        keywords: copy.keywords,
        path,
        image: `${SITE_URL}/og-image.png`,
        imageAlt: `${meta.display} Common Number Today – Direct, House and Ending`,
        breadcrumbs: [
            { name: meta.display, path: resultTodayPath(slug) },
            { name: "Common Number", path },
        ],
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: copy.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    const [pred, setPred] = useState(null);
    const [loading, setLoading] = useState(true);
    const dateStr = todayIST();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/common-number/today", {
                params: { market_key: marketKey },
            });
            setPred(data || null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketKey]);

    const row = (label, direct, house, ending) => (
        <div className="grid grid-cols-12 border-t border-slate-200">
            <div className="col-span-3 sm:col-span-2 px-3 py-3 bg-slate-50 text-center text-xs sm:text-sm font-semibold text-slate-700 flex items-center justify-center">
                {label}
            </div>
            <div className="col-span-3 sm:col-span-4 px-3 py-3 text-center result-digit text-base text-slate-900" data-testid={`cn-${label.replace('/','').toLowerCase()}-direct`}>
                {direct && direct.length ? direct.join(", ") : "—"}
            </div>
            <div className="col-span-3 px-3 py-3 text-center result-digit text-base text-slate-900" data-testid={`cn-${label.replace('/','').toLowerCase()}-house`}>
                {house || "—"}
            </div>
            <div className="col-span-3 px-3 py-3 text-center result-digit text-base text-slate-900" data-testid={`cn-${label.replace('/','').toLowerCase()}-ending`}>
                {ending || "—"}
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid={`page-common-${slug}`}>
            <Breadcrumbs
                items={[
                    { name: meta.display, path: resultTodayPath(slug) },
                    { name: "Common Number", path },
                ]}
            />

            {/* Hero */}
            <header className="mt-4 rounded-2xl overflow-hidden card-crisp">
                <div
                    className="px-5 py-4 md:px-8 md:py-6 text-white"
                    style={{ background: cardMeta?.accent || "#1E3A8A" }}
                >
                    <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
                        Common Number · {prettyDate(pred?.date || dateStr)}
                    </div>
                    <h1
                        className="font-display font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight"
                        data-testid="page-h1"
                    >
                        {copy.h1}
                    </h1>
                </div>

                <div className="bg-white">
                    <div className="grid grid-cols-12 bg-slate-100 border-b border-slate-200">
                        <div className="col-span-3 sm:col-span-2 px-3 py-2 text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Round</div>
                        <div className="col-span-3 sm:col-span-4 px-3 py-2 text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Direct</div>
                        <div className="col-span-3 px-3 py-2 text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold">House</div>
                        <div className="col-span-3 px-3 py-2 text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ending</div>
                    </div>
                    {row(`F/R (${meta.frTime})`, pred?.fr_direct, pred?.fr_house, pred?.fr_ending)}
                    {row(`S/R (${meta.srTime})`, pred?.sr_direct, pred?.sr_house, pred?.sr_ending)}
                </div>

                <div className="bg-slate-50 border-t border-slate-200 px-4 py-2 text-[11px] text-slate-500">
                    {loading
                        ? "Loading…"
                        : pred?.updated_at
                        ? `Auto-updated for today · ${new Date(pred.updated_at).toLocaleString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              day: "2-digit",
                              month: "short",
                          })} · For information only`
                        : "Preparing today's common number…"}
                </div>
            </header>

            {/* About */}
            <section className="mt-8">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    About {meta.display} Common Number
                </h2>
                <p className="text-slate-700 mt-2 leading-relaxed">
                    The {meta.display} common number shown above is an <strong>automatically generated daily
                    display</strong>. It is provided for <strong>informational and entertainment purposes only</strong>.
                    It is <strong>not</strong> a guaranteed winning number, a fixed number or a sure-shot prediction.
                </p>
                <p className="text-slate-700 mt-3 leading-relaxed">
                    Numbers are presented in three formats — <strong>Direct</strong> (2-digit),
                    <strong> House</strong> (tens digit) and <strong>Ending</strong> (units digit) — for both First
                    Round ({meta.frTime}) and Second Round ({meta.srTime}). The same values are shown to every
                    visitor throughout the day and refresh automatically after the daily 1:00 AM IST cycle.
                </p>
            </section>

            {/* Cross-links */}
            <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CrossLink
                    to={resultTodayPath(slug)}
                    title={`${meta.display} Result Today`}
                    desc="Live FR & SR result"
                    testid="cta-result-today"
                />
                <CrossLink
                    to={previousResultPath(slug)}
                    title={`${meta.display} Previous Results`}
                    desc="FR & SR history archive"
                    testid="cta-previous-result"
                />
            </section>

            {/* FAQ */}
            <section className="mt-10">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    Frequently Asked Questions
                </h2>
                <div className="mt-3 space-y-3">
                    {copy.faq.map((f, i) => (
                        <details
                            key={i}
                            className="card-crisp p-4 open:shadow-md"
                            data-testid={`faq-${i}`}
                        >
                            <summary className="cursor-pointer font-semibold text-blue-950">
                                {f.q}
                            </summary>
                            <p className="mt-2 text-slate-700 leading-relaxed text-sm">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <div className="mt-8 rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900 flex gap-3">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p>
                    Disclaimer: {meta.display} common number information is shared only for reference. It is
                    not a guarantee of any outcome. Please follow all local laws applicable to you.
                </p>
            </div>

            <div className="mt-6 text-center">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 hover:underline"
                >
                    ← Back to All Teer Results
                </Link>
            </div>
        </div>
    );
};

const CrossLink = ({ to, title, desc, testid }) => (
    <Link
        to={to}
        className="card-crisp p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={testid}
    >
        <div>
            <div className="font-display font-bold text-blue-950 tracking-tight">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-blue-900" />
    </Link>
);

export default GameCommonNumber;
